import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FiPlay, FiRefreshCw, FiCheck, FiAlertCircle, FiClock, FiDatabase, FiActivity, FiUsers, FiInfo, FiTrendingUp, FiTarget } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  LoadingSpinner,
  Input 
} from '../components/ui'
import { theme, mediaQuery } from '../theme'
import { analyticsAPI } from '../services/api'
import ResilienceTimeline from '../components/ResilienceTimeline'

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const AnalysisCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.main}10, ${theme.colors.secondary.green}10);
  border: 2px solid ${theme.colors.primary.main}30;
`

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${props => {
    switch(props.status) {
      case 'success': return theme.colors.status.success + '20';
      case 'error': return theme.colors.status.error + '20';
      case 'warning': return theme.colors.status.warning + '20';
      default: return theme.colors.primary.light + '20';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'success': return theme.colors.status.success;
      case 'error': return theme.colors.status.error;
      case 'warning': return theme.colors.status.warning;
      default: return theme.colors.primary.main;
    }
  }};
  font-weight: 500;
`

const LogContainer = styled.div`
  background: ${theme.colors.secondary.black};
  color: ${theme.colors.secondary.beige};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.primary.dark};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary.light};
    border-radius: 4px;
  }
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.secondary.beige};
  border-radius: 4px;
  overflow: hidden;
  margin: ${theme.spacing.sm} 0;
`

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.primary.main};
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`

const DiagnosticGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`

const DiagnosticItem = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${props => {
    switch(props.status) {
      case 'healthy': return theme.colors.status.success;
      case 'warning': return theme.colors.status.warning;
      case 'error': return theme.colors.status.error;
      default: return theme.colors.primary.light;
    }
  }};
`

const DiagnosticLabel = styled.div`
  font-weight: 600;
  color: ${theme.colors.secondary.black};
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const DiagnosticValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.xs};
`

const DiagnosticDescription = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
`

const UserIdInput = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
  }
`

const ConfidenceIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${props => {
    switch(props.level) {
      case 'very-high': return theme.colors.status.success + '20';
      case 'high': return theme.colors.status.success + '15';
      case 'moderate': return theme.colors.status.warning + '20';
      case 'low': return theme.colors.status.error + '15';
      case 'very-low': return theme.colors.status.error + '20';
      default: return theme.colors.primary.light + '20';
    }
  }};
  color: ${props => {
    switch(props.level) {
      case 'very-high': return theme.colors.status.success;
      case 'high': return theme.colors.status.success;
      case 'moderate': return theme.colors.status.warning;
      case 'low': return theme.colors.status.error;
      case 'very-low': return theme.colors.status.error;
      default: return theme.colors.primary.main;
    }
  }};
  font-weight: 500;
  font-size: ${theme.typography.body.p3.fontSize};
  margin-top: ${theme.spacing.sm};
`

const ConfidenceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch(props.level) {
      case 'very-high': return theme.colors.status.success + '30';
      case 'high': return theme.colors.status.success + '25';
      case 'moderate': return theme.colors.status.warning + '30';
      case 'low': return theme.colors.status.error + '25';
      case 'very-low': return theme.colors.status.error + '30';
      default: return theme.colors.primary.light + '30';
    }
  }};
  color: ${props => {
    switch(props.level) {
      case 'very-high': return theme.colors.status.success;
      case 'high': return theme.colors.status.success;
      case 'moderate': return theme.colors.status.warning;
      case 'low': return theme.colors.status.error;
      case 'very-low': return theme.colors.status.error;
      default: return theme.colors.primary.main;
    }
  }};
`

const StatisticalDetails = styled.div`
  background: ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
  border-left: 4px solid ${theme.colors.primary.main};
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.xs} 0;
  border-bottom: 1px solid ${theme.colors.primary.light}20;
  
  &:last-child {
    border-bottom: none;
  }
`

const StatLabel = styled.span`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
  font-weight: 500;
`

const StatValue = styled.span`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.secondary.black};
  font-weight: 600;
`

const AnalysisPage = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisProgress, setAnalysisProgress] = useState(0)
  const [analysisLogs, setAnalysisLogs] = useState('')
  const [diagnostics, setDiagnostics] = useState(null)
  const [systemHealth, setSystemHealth] = useState(null)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [userAnalysis, setUserAnalysis] = useState(null)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  
  useEffect(() => {
    loadDiagnostics()
    loadSystemHealth()
  }, [])
  
  const loadDiagnostics = async () => {
    try {
      const response = await analyticsAPI.diagnose()
      setDiagnostics(response.data)
    } catch (err) {
      console.error('Failed to load diagnostics:', err)
    }
  }
  
  const loadSystemHealth = async () => {
    try {
      const response = await analyticsAPI.detailedHealth()
      setSystemHealth(response.data)
    } catch (err) {
      console.error('Failed to load system health:', err)
    }
  }
  
  const handleRunFullAnalysis = async () => {
    setIsAnalyzing(true)
    setAnalysisProgress(0)
    setAnalysisLogs('🚀 Starting full dataset analysis...\n\n')
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 90) return prev
        return prev + Math.random() * 10
      })
    }, 500)
    
    try {
      const response = await analyticsAPI.analyzeAll()
      
      setAnalysisProgress(100)
      setAnalysisLogs(prev => prev + '✅ Analysis completed successfully!\n\n' + JSON.stringify(response.data, null, 2))
      
      // Reload diagnostics after analysis
      setTimeout(() => {
        loadDiagnostics()
        loadSystemHealth()
      }, 1000)
      
    } catch (err) {
      setAnalysisLogs(prev => prev + '❌ Analysis failed: ' + err.message + '\n\n' + JSON.stringify(err.response?.data || {}, null, 2))
    } finally {
      clearInterval(progressInterval)
      setIsAnalyzing(false)
      setAnalysisProgress(100)
    }
  }
  
  const handleTestPipeline = async () => {
    setAnalysisLogs('🧪 Running pipeline test...\n\n')
    
    try {
      const response = await analyticsAPI.testPipeline()
      setAnalysisLogs(prev => prev + '✅ Pipeline test completed!\n\n' + JSON.stringify(response.data, null, 2))
    } catch (err) {
      setAnalysisLogs(prev => prev + '❌ Pipeline test failed: ' + err.message + '\n\n' + JSON.stringify(err.response?.data || {}, null, 2))
    }
  }
  
  const handleUserAnalysis = async () => {
    if (!selectedUserId.trim()) return
    
    setIsLoadingUser(true)
    setUserAnalysis(null)
    
    try {
      const response = await analyticsAPI.getUserAnalysis(selectedUserId.trim())
      setUserAnalysis(response.data)
      setAnalysisLogs(prev => prev + `📊 User analysis for ${selectedUserId}:\n\n` + JSON.stringify(response.data, null, 2) + '\n\n')
    } catch (err) {
      setAnalysisLogs(prev => prev + `❌ User analysis failed: ${err.message}\n\n`)
    } finally {
      setIsLoadingUser(false)
    }
  }
  
  const getDiagnosticStatus = (value, type) => {
    switch (type) {
      case 'entries':
        return value > 1000 ? 'healthy' : value > 100 ? 'warning' : 'error'
      case 'users':
        return value > 100 ? 'healthy' : value > 10 ? 'warning' : 'error'
      case 'quality':
        return value > 50 ? 'healthy' : value > 20 ? 'warning' : 'error'
      case 'response':
        return value < 1000 ? 'healthy' : value < 3000 ? 'warning' : 'error'
      default:
        return 'healthy'
    }
  }
  
  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const getConfidenceIcon = (level) => {
    switch (level) {
      case 'very-high':
      case 'high':
        return <FiCheck />
      case 'moderate':
        return <FiInfo />
      case 'low':
      case 'very-low':
        return <FiAlertCircle />
      default:
        return <FiInfo />
    }
  }

  const getConfidenceLabel = (level) => {
    switch (level) {
      case 'very-high': return 'Very High Confidence'
      case 'high': return 'High Confidence'
      case 'moderate': return 'Moderate Confidence'
      case 'low': return 'Low Confidence'
      case 'very-low': return 'Very Low Confidence'
      default: return 'Unknown Confidence'
    }
  }

  const formatPValue = (pValue) => {
    if (pValue < 0.001) return '< 0.001'
    if (pValue < 0.01) return '< 0.01'
    if (pValue < 0.05) return '< 0.05'
    return pValue.toFixed(3)
  }
  
  return (
    <Container>
      <PageHeader>
        <div>
          <Heading1>Analysis & Diagnostics</Heading1>
          <BodyText>
            Run batch analysis and monitor system health
          </BodyText>
        </div>
        
        <Button onClick={loadDiagnostics} variant="outline">
          <FiRefreshCw style={{ marginRight: '8px' }} />
          Refresh
        </Button>
      </PageHeader>
      
      {/* System Health Overview */}
      {systemHealth && (
        <Card>
          <Heading2>System Health</Heading2>
          <DiagnosticGrid>
            <DiagnosticItem status="healthy">
              <DiagnosticLabel>
                <FiCheck />
                System Status
              </DiagnosticLabel>
              <DiagnosticValue>Online</DiagnosticValue>
              <DiagnosticDescription>All systems operational</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status="healthy">
              <DiagnosticLabel>
                <FiDatabase />
                BigQuery Connection
              </DiagnosticLabel>
              <DiagnosticValue>Connected</DiagnosticValue>
              <DiagnosticDescription>founders-first project</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status="healthy">
              <DiagnosticLabel>
                <FiClock />
                Uptime
              </DiagnosticLabel>
              <DiagnosticValue>{formatDuration(systemHealth.uptime || 0)}</DiagnosticValue>
              <DiagnosticDescription>Since last restart</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status="healthy">
              <DiagnosticLabel>
                <FiActivity />
                Memory Usage
              </DiagnosticLabel>
              <DiagnosticValue>{Math.round((systemHealth.memory?.used / systemHealth.memory?.total) * 100) || 0}%</DiagnosticValue>
              <DiagnosticDescription>{Math.round((systemHealth.memory?.used / 1024 / 1024) || 0)}MB used</DiagnosticDescription>
            </DiagnosticItem>
          </DiagnosticGrid>
        </Card>
      )}
      
      {/* Data Quality Diagnostics */}
      {diagnostics && (
        <Card>
          <Heading2>Data Quality Diagnostics</Heading2>
          <DiagnosticGrid>
            <DiagnosticItem status={getDiagnosticStatus(diagnostics.totalEntries, 'entries')}>
              <DiagnosticLabel>
                <FiDatabase />
                Total Entries
              </DiagnosticLabel>
              <DiagnosticValue>{diagnostics.totalEntries?.toLocaleString()}</DiagnosticValue>
              <DiagnosticDescription>Journal entries in database</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status={getDiagnosticStatus(diagnostics.validEntries, 'entries')}>
              <DiagnosticLabel>
                <FiCheck />
                Valid Entries
              </DiagnosticLabel>
              <DiagnosticValue>{diagnostics.validEntries?.toLocaleString()}</DiagnosticValue>
              <DiagnosticDescription>Entries with pressure + emotions</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status={getDiagnosticStatus(diagnostics.uniqueUsers, 'users')}>
              <DiagnosticLabel>
                <FiUsers />
                Unique Users
              </DiagnosticLabel>
              <DiagnosticValue>{diagnostics.uniqueUsers?.toLocaleString()}</DiagnosticValue>
              <DiagnosticDescription>Users with journal data</DiagnosticDescription>
            </DiagnosticItem>
            
            <DiagnosticItem status={getDiagnosticStatus(diagnostics.dataQuality, 'quality')}>
              <DiagnosticLabel>
                <FiActivity />
                Data Quality
              </DiagnosticLabel>
              <DiagnosticValue>{Math.round(diagnostics.dataQuality)}%</DiagnosticValue>
              <DiagnosticDescription>Entries meeting requirements</DiagnosticDescription>
            </DiagnosticItem>
          </DiagnosticGrid>
          
          {diagnostics.emotionalFactorCoverage && (
            <div>
              <Heading2 style={{ fontSize: '18px', marginTop: theme.spacing.lg, marginBottom: theme.spacing.md }}>
                Emotional Factor Coverage
              </Heading2>
              <Grid columns="repeat(auto-fit, minmax(150px, 1fr))">
                {Object.entries(diagnostics.emotionalFactorCoverage).map(([factor, percentage]) => (
                  <DiagnosticItem key={factor} status={getDiagnosticStatus(percentage, 'quality')}>
                    <DiagnosticLabel style={{ textTransform: 'capitalize' }}>
                      {factor}
                    </DiagnosticLabel>
                    <DiagnosticValue>{Math.round(percentage)}%</DiagnosticValue>
                  </DiagnosticItem>
                ))}
              </Grid>
            </div>
          )}
        </Card>
      )}
      
      {/* Analysis Controls */}
      <AnalysisCard>
        <Heading2>Batch Analysis</Heading2>
        <BodyText style={{ marginBottom: theme.spacing.lg }}>
          Run comprehensive analysis on the entire dataset to calculate mental health metrics for all users.
        </BodyText>
        
        <ActionButton 
          onClick={handleRunFullAnalysis} 
          disabled={isAnalyzing}
          variant="secondary"
          fullWidth
        >
          {isAnalyzing ? <LoadingSpinner style={{ width: '20px', height: '20px', margin: '0' }} /> : <FiPlay />}
          {isAnalyzing ? 'Running Analysis...' : 'Run Full Analysis'}
        </ActionButton>
        
        <ActionButton 
          onClick={handleTestPipeline} 
          disabled={isAnalyzing}
          variant="outline"
          fullWidth
        >
          <FiCheck />
          Test Pipeline
        </ActionButton>
        
        {isAnalyzing && (
          <div>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              Progress: {Math.round(analysisProgress)}%
            </BodyText>
            <ProgressBar>
              <ProgressFill progress={analysisProgress} />
            </ProgressBar>
          </div>
        )}
      </AnalysisCard>
      
      {/* Individual User Analysis */}
      <Card>
        <Heading2>Individual User Analysis</Heading2>
        <BodyText style={{ marginBottom: theme.spacing.md }}>
          Analyze metrics for a specific user by entering their User ID.
        </BodyText>
        
        <UserIdInput>
          <Input
            placeholder="Enter User ID (e.g., 0408wU6PcMM8GiB7xXpNlPXFfei2)"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button 
            onClick={handleUserAnalysis}
            disabled={isLoadingUser || !selectedUserId.trim()}
          >
            {isLoadingUser ? <LoadingSpinner style={{ width: '16px', height: '16px', margin: '0' }} /> : 'Analyze User'}
          </Button>
        </UserIdInput>
        
        {userAnalysis && (
          <div>
            <Heading2 style={{ fontSize: '18px', marginTop: theme.spacing.lg, marginBottom: theme.spacing.md }}>
              User Analysis Results - {userAnalysis.userId}
            </Heading2>
            <Grid columns="repeat(auto-fit, minmax(200px, 1fr))">
              {/* Mental Resilience */}
              {userAnalysis.mentalResilience && userAnalysis.mentalResilience.score !== null && (
                <DiagnosticItem status="healthy">
                  <DiagnosticLabel>
                    Mental Resilience
                    {userAnalysis.mentalResilience.confidence && (
                      <ConfidenceBadge level={userAnalysis.mentalResilience.confidence.level}>
                        {getConfidenceIcon(userAnalysis.mentalResilience.confidence.level)}
                      </ConfidenceBadge>
                    )}
                  </DiagnosticLabel>
                  <DiagnosticValue>{Math.round(userAnalysis.mentalResilience.score * 100) / 100}</DiagnosticValue>
                  <DiagnosticDescription>{userAnalysis.mentalResilience.interpretation}</DiagnosticDescription>
                </DiagnosticItem>
              )}
              
              {/* Entry Count */}
              <DiagnosticItem status="healthy">
                <DiagnosticLabel>Valid Entries</DiagnosticLabel>
                <DiagnosticValue>{userAnalysis.validEntries}</DiagnosticValue>
                <DiagnosticDescription>Journal entries analyzed</DiagnosticDescription>
              </DiagnosticItem>
              
              {/* Total Entries */}
              <DiagnosticItem status="info">
                <DiagnosticLabel>Total Entries</DiagnosticLabel>
                <DiagnosticValue>{userAnalysis.totalEntries}</DiagnosticValue>
                <DiagnosticDescription>All journal entries found</DiagnosticDescription>
              </DiagnosticItem>
            </Grid>
            
            {/* Additional metric details */}
            {userAnalysis.mentalResilience && userAnalysis.mentalResilience.score !== null && (
              <div style={{ marginTop: theme.spacing.lg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.md }}>
                  <Heading2 style={{ fontSize: '18px', margin: 0 }}>
                    Mental Resilience Details
                  </Heading2>
                  {userAnalysis.mentalResilience.confidence && (
                    <ConfidenceBadge level={userAnalysis.mentalResilience.confidence.level}>
                      {getConfidenceIcon(userAnalysis.mentalResilience.confidence.level)}
                      {getConfidenceLabel(userAnalysis.mentalResilience.confidence.level)}
                    </ConfidenceBadge>
                  )}
                </div>

                {userAnalysis.mentalResilience.confidence && (
                  <ConfidenceIndicator level={userAnalysis.mentalResilience.confidence.level}>
                    {getConfidenceIcon(userAnalysis.mentalResilience.confidence.level)}
                    {userAnalysis.mentalResilience.confidence.message}
                  </ConfidenceIndicator>
                )}

                <StatisticalDetails>
                  <BodyText style={{ marginBottom: theme.spacing.sm, fontWeight: 600 }}>
                    <FiTrendingUp style={{ marginRight: theme.spacing.xs }} />
                    Statistical Analysis
                  </BodyText>
                  
                  <StatGrid>
                    <StatItem>
                      <StatLabel>Correlation Strength (R²)</StatLabel>
                      <StatValue>{Math.round(userAnalysis.mentalResilience.rSquared * 1000) / 1000}</StatValue>
                    </StatItem>
                    
                    <StatItem>
                      <StatLabel>Pressure Sensitivity</StatLabel>
                      <StatValue>{Math.round(userAnalysis.mentalResilience.slope * 1000) / 1000}</StatValue>
                    </StatItem>
                    
                    <StatItem>
                      <StatLabel>Data Points</StatLabel>
                      <StatValue>{userAnalysis.mentalResilience.dataPoints} entries</StatValue>
                    </StatItem>

                    {userAnalysis.mentalResilience.confidence?.statistical && (
                      <>
                        <StatItem>
                          <StatLabel>Statistical Significance</StatLabel>
                          <StatValue>
                            p = {formatPValue(userAnalysis.mentalResilience.confidence.statistical.pValue)}
                          </StatValue>
                        </StatItem>
                        
                        <StatItem>
                          <StatLabel>Sample Size Quality</StatLabel>
                          <StatValue style={{ textTransform: 'capitalize' }}>
                            {userAnalysis.mentalResilience.confidence.statistical.sampleSizeAdequacy}
                          </StatValue>
                        </StatItem>
                        
                        <StatItem>
                          <StatLabel>Margin of Error</StatLabel>
                          <StatValue>
                            ±{userAnalysis.mentalResilience.confidence.statistical.marginOfError}
                          </StatValue>
                        </StatItem>
                      </>
                    )}
                  </StatGrid>

                  {userAnalysis.mentalResilience.confidence?.statistical?.confidenceInterval && (
                    <div style={{ marginTop: theme.spacing.md, padding: theme.spacing.sm, backgroundColor: theme.colors.primary.main + '10', borderRadius: theme.borderRadius.sm }}>
                      <SmallText style={{ marginBottom: theme.spacing.xs, fontWeight: 600 }}>
                        95% Confidence Interval for Pressure Sensitivity:
                      </SmallText>
                      <SmallText>
                        {userAnalysis.mentalResilience.confidence.statistical.confidenceInterval.lower} to{' '}
                        {userAnalysis.mentalResilience.confidence.statistical.confidenceInterval.upper}
                      </SmallText>
                      <SmallText style={{ marginTop: theme.spacing.xs, fontStyle: 'italic' }}>
                        We can be 95% confident that the true pressure sensitivity falls within this range.
                      </SmallText>
                    </div>
                  )}

                  <div style={{ marginTop: theme.spacing.md, padding: theme.spacing.sm, backgroundColor: theme.colors.secondary.beige, borderRadius: theme.borderRadius.sm }}>
                    <SmallText style={{ fontStyle: 'italic', color: theme.colors.primary.light }}>
                      <strong>Interpretation Guide:</strong> Higher R² values (closer to 1.0) indicate stronger correlation. 
                      Positive slope means emotions improve with pressure; negative slope means emotions decline with pressure. 
                      Statistical significance (p &lt; 0.05) indicates the relationship is unlikely due to chance.
                    </SmallText>
                  </div>
                </StatisticalDetails>
              </div>
            )}

            {/* Resilience Timeline */}
            <ResilienceTimeline userId={selectedUserId} />
          </div>
        )}
      </Card>
      
      {/* Analysis Logs */}
      <Card>
        <Heading2>Analysis Logs</Heading2>
        <BodyText style={{ marginBottom: theme.spacing.md }}>
          Real-time logs from analysis operations and system diagnostics.
        </BodyText>
        
        <LogContainer>
          {analysisLogs || 'No recent analysis operations. Click "Run Full Analysis" or "Test Pipeline" to see logs here.'}
        </LogContainer>
      </Card>
    </Container>
  )
}

export default AnalysisPage 